import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { employeesAPI, departmentsAPI } from '../lib/api';
import { ArrowLeft, Save, Loader2, AlertTriangle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { TimeAttendanceSection, LeaveAccrualSection } from '../components/employee/EmployeeFormSections';

const NON_PERMANENT_TYPES = ['Temporary', 'Fixed-Term Contract', 'Contractor', 'Subcontractor', 'Casual/Seasonal'];

export default function EmployeeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({
    full_name: '',
    id_number: '',
    date_of_birth: '',
    email: '',
    phone: '',
    physical_address: '',
    residential_address: '',
    department: '',
    job_title: '',
    line_manager: '',
    employment_type: 'Permanent',
    start_date: '',
    probation_end_date: '',
    salary: '',
    status: 'Active',
    site_location: '',
    // Time & Attendance
    is_hourly_paid: false,
    hourly_pay_cycle: '',  // Fortnightly or Monthly
    shift_type: 'Day Shift',  // Day Shift or Night Shift
    // Leave Accrual anchors (admin-settable)
    annual_leave_accrual_start: '',
    sick_cycle_start: '',
    // Next of Kin
    next_of_kin_name: '',
    next_of_kin_relationship: '',
    next_of_kin_phone: '',
    // Bank Account
    bank_name: '',
    bank_account_number: '',
    bank_account_type: '',
    // Contract Details (for non-permanent)
    contract_start_date: '',
    contract_end_date: '',
    contract_renewal_option: false,
    renewal_status: 'Not reviewed',
    contracting_company: '',
    contract_supervisor: '',
    contract_reason: '',
  });
  
  const isNonPermanent = NON_PERMANENT_TYPES.includes(formData.employment_type);

  useEffect(() => {
    fetchDepartments();
    if (isEditing) {
      fetchEmployee();
    }
  }, [id]);

  const fetchDepartments = async () => {
    try {
      const res = await departmentsAPI.getAll();
      setDepartments(res.data);
    } catch (error) {
      console.error('Failed to load departments');
    }
  };

  const fetchEmployee = async () => {
    try {
      const res = await employeesAPI.getById(id);
      setFormData({
        ...res.data,
        salary: res.data.salary?.toString() || '',
      });
    } catch (error) {
      toast.error('Failed to load employee');
      navigate('/employees');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation for hourly employees
    if (formData.is_hourly_paid) {
      if (!formData.hourly_pay_cycle) {
        toast.error('Pay Cycle is required for hourly-paid employees');
        return;
      }
      // shift_type is advisory only - actual shift is auto-detected from clock-in time
    }

    setSaving(true);

    const payload = {
      ...formData,
      salary: parseFloat(formData.salary) || 0,
      annual_leave_accrual_start: formData.annual_leave_accrual_start || null,
      sick_cycle_start: formData.sick_cycle_start || null,
    };

    try {
      if (isEditing) {
        await employeesAPI.update(id, payload);
        toast.success('Employee updated');
      } else {
        await employeesAPI.create(payload);
        toast.success('Employee created');
      }
      navigate('/employees');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save employee');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 page-transition max-w-4xl" data-testid="employee-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/employees')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
            {isEditing ? 'Edit Employee' : 'Add New Employee'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isEditing ? 'Update employee information' : 'Create a new employee record'}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Personal Information */}
        <div className="form-section">
          <h2 className="form-section-title">Personal Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => handleChange('full_name', e.target.value)}
                required
                data-testid="input-full-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="id_number">ID Number *</Label>
              <Input
                id="id_number"
                value={formData.id_number}
                onChange={(e) => handleChange('id_number', e.target.value)}
                required
                data-testid="input-id-number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="date_of_birth">Date of Birth *</Label>
              <Input
                id="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => handleChange('date_of_birth', e.target.value)}
                required
                data-testid="input-dob"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                data-testid="input-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                required
                data-testid="input-phone"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="physical_address">Physical Address *</Label>
              <Input
                id="physical_address"
                value={formData.physical_address}
                onChange={(e) => handleChange('physical_address', e.target.value)}
                required
                data-testid="input-address"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="residential_address">Residential Address</Label>
              <Input
                id="residential_address"
                value={formData.residential_address}
                onChange={(e) => handleChange('residential_address', e.target.value)}
                data-testid="input-residential-address"
              />
            </div>
          </div>
        </div>

        {/* Next of Kin */}
        <div className="form-section">
          <h2 className="form-section-title">Next of Kin</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="next_of_kin_name">Full Name</Label>
              <Input
                id="next_of_kin_name"
                value={formData.next_of_kin_name}
                onChange={(e) => handleChange('next_of_kin_name', e.target.value)}
                data-testid="input-nok-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="next_of_kin_relationship">Relationship</Label>
              <Select 
                value={formData.next_of_kin_relationship} 
                onValueChange={(v) => handleChange('next_of_kin_relationship', v)}
              >
                <SelectTrigger data-testid="select-nok-relationship">
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Spouse">Spouse</SelectItem>
                  <SelectItem value="Parent">Parent</SelectItem>
                  <SelectItem value="Sibling">Sibling</SelectItem>
                  <SelectItem value="Child">Child</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="next_of_kin_phone">Phone Number</Label>
              <Input
                id="next_of_kin_phone"
                value={formData.next_of_kin_phone}
                onChange={(e) => handleChange('next_of_kin_phone', e.target.value)}
                data-testid="input-nok-phone"
              />
            </div>
          </div>
        </div>

        {/* Bank Account */}
        <div className="form-section">
          <h2 className="form-section-title">Bank Account Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bank_name">Bank Name</Label>
              <Input
                id="bank_name"
                value={formData.bank_name}
                onChange={(e) => handleChange('bank_name', e.target.value)}
                data-testid="input-bank-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bank_account_number">Account Number</Label>
              <Input
                id="bank_account_number"
                value={formData.bank_account_number}
                onChange={(e) => handleChange('bank_account_number', e.target.value)}
                data-testid="input-bank-account"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bank_account_type">Account Type</Label>
              <Select 
                value={formData.bank_account_type} 
                onValueChange={(v) => handleChange('bank_account_type', v)}
              >
                <SelectTrigger data-testid="select-bank-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Savings">Savings</SelectItem>
                  <SelectItem value="Checking">Checking</SelectItem>
                  <SelectItem value="Current">Current</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Contract Details (only for non-permanent) */}
        {isNonPermanent && (
          <div className="form-section border-amber-500/30 bg-amber-500/5">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <h2 className="form-section-title text-amber-400 !mb-0">Contract Details</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Required for non-permanent employees</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contract_start_date">Contract Start Date *</Label>
                <Input
                  id="contract_start_date"
                  type="date"
                  value={formData.contract_start_date}
                  onChange={(e) => handleChange('contract_start_date', e.target.value)}
                  required={isNonPermanent}
                  data-testid="input-contract-start"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contract_end_date">Contract End Date *</Label>
                <Input
                  id="contract_end_date"
                  type="date"
                  value={formData.contract_end_date}
                  onChange={(e) => handleChange('contract_end_date', e.target.value)}
                  required={isNonPermanent}
                  data-testid="input-contract-end"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contracting_company">Contracting Company</Label>
                <Input
                  id="contracting_company"
                  value={formData.contracting_company}
                  onChange={(e) => handleChange('contracting_company', e.target.value)}
                  placeholder="e.g., ABC Staffing Agency"
                  data-testid="input-contracting-company"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contract_supervisor">Contract Supervisor</Label>
                <Input
                  id="contract_supervisor"
                  value={formData.contract_supervisor}
                  onChange={(e) => handleChange('contract_supervisor', e.target.value)}
                  placeholder="Person managing this contract"
                  data-testid="input-contract-supervisor"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="contract_reason">Reason for Contract Employment</Label>
                <Textarea
                  id="contract_reason"
                  value={formData.contract_reason}
                  onChange={(e) => handleChange('contract_reason', e.target.value)}
                  placeholder="e.g., Seasonal demand, Project-based work, Maternity cover"
                  className="min-h-[80px]"
                  data-testid="input-contract-reason"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="contract_renewal_option"
                  checked={formData.contract_renewal_option}
                  onCheckedChange={(v) => handleChange('contract_renewal_option', v)}
                  data-testid="checkbox-renewal-option"
                />
                <Label htmlFor="contract_renewal_option" className="text-sm font-normal">
                  Contract has renewal option
                </Label>
              </div>
              {isEditing && (
                <div className="space-y-2">
                  <Label htmlFor="renewal_status">Renewal Status</Label>
                  <Select 
                    value={formData.renewal_status || 'Not reviewed'} 
                    onValueChange={(v) => handleChange('renewal_status', v)}
                  >
                    <SelectTrigger data-testid="select-renewal-status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Not reviewed">Not reviewed</SelectItem>
                      <SelectItem value="Under review">Under review</SelectItem>
                      <SelectItem value="Renewed">Renewed</SelectItem>
                      <SelectItem value="Expiring">Expiring</SelectItem>
                      <SelectItem value="Expired">Expired</SelectItem>
                      <SelectItem value="Terminated">Terminated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Employment Details */}
        <div className="form-section">
          <h2 className="form-section-title">Employment Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="department">Department *</Label>
              <Select 
                value={formData.department} 
                onValueChange={(v) => handleChange('department', v)}
              >
                <SelectTrigger data-testid="select-department">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="job_title">Job Title *</Label>
              <Input
                id="job_title"
                value={formData.job_title}
                onChange={(e) => handleChange('job_title', e.target.value)}
                required
                data-testid="input-job-title"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="line_manager">Line Manager</Label>
              <Input
                id="line_manager"
                value={formData.line_manager}
                onChange={(e) => handleChange('line_manager', e.target.value)}
                data-testid="input-line-manager"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="employment_type">Employment Type *</Label>
              <Select 
                value={formData.employment_type} 
                onValueChange={(v) => handleChange('employment_type', v)}
              >
                <SelectTrigger data-testid="select-employment-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Permanent">Permanent</SelectItem>
                  <SelectItem value="Temporary">Temporary</SelectItem>
                  <SelectItem value="Fixed-Term Contract">Fixed-Term Contract</SelectItem>
                  <SelectItem value="Contractor">Contractor</SelectItem>
                  <SelectItem value="Subcontractor">Subcontractor</SelectItem>
                  <SelectItem value="Casual/Seasonal">Casual/Seasonal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="site_location">Site Location</Label>
              <Input
                id="site_location"
                value={formData.site_location}
                onChange={(e) => handleChange('site_location', e.target.value)}
                data-testid="input-site-location"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="start_date">Start Date *</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => handleChange('start_date', e.target.value)}
                required
                data-testid="input-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="probation_end_date">Probation End Date</Label>
              <Input
                id="probation_end_date"
                type="date"
                value={formData.probation_end_date}
                onChange={(e) => handleChange('probation_end_date', e.target.value)}
                data-testid="input-probation-end"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="salary">Salary *</Label>
              <Input
                id="salary"
                type="number"
                value={formData.salary}
                onChange={(e) => handleChange('salary', e.target.value)}
                required
                data-testid="input-salary"
              />
            </div>
            {isEditing && (
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(v) => handleChange('status', v)}
                >
                  <SelectTrigger data-testid="select-status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        <TimeAttendanceSection formData={formData} handleChange={handleChange} />

        <LeaveAccrualSection formData={formData} handleChange={handleChange} />

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/employees')}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving} data-testid="save-employee-btn">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? 'Update Employee' : 'Create Employee'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
